import {
  makeStyles,
  mergeClasses,
  tokens,
  shorthands,
  Button,
  TabList,
  Tab,
  Text,
  Spinner,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Badge,
} from '@coreai/fluentui-react';
import {
  MoreHorizontalRegular,
  BugRegular,
} from '@fluentui/react-icons';
import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { ConnectionErrorBanner } from '../compact/ConnectionErrorBanner';
import { WorkflowInputPanel } from './WorkflowInputPanel';
import { RunDataPanel } from '../run-data/RunDataPanel';
import { useNodeTokenSources } from '@/hooks/useNodeTokenSources';
import { DynamicContentTokensContext, tokenSourcesToSuggestions } from '../fields/dynamicContentTokens';
import type { Token } from '@/types/valueSegment';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { useTabOverflow } from './useTabOverflow';
import { NodeEditorTabBody } from './NodeEditorTabBody';
import { useNodeEditorContext } from './useNodeEditorContext';
import type { EditorTab } from './useNodeEditorContext';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },

  // Main content area with three panels
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },

  // Left panel - Input (resizable)
  leftPanel: {
    flexShrink: 0,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    position: 'relative',
  },

  // Center panel - Editor
  centerPanel: {
    flex: 1,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: '400px',
  },
  // Modifier for the configure-tab usage where the editor must be allowed to
  // shrink below 400px (e.g. when the right/left side panels claim more space).
  centerPanelShrinkable: {
    minWidth: 0,
  },

  // Right panel - Output (resizable)
  rightPanel: {
    flexShrink: 0,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
    position: 'relative',
  },

  // Resizer handle
  resizer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '6px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    zIndex: 10,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorBrandBackground,
    },
  },
  resizerLeft: {
    right: '-3px',
  },
  resizerRight: {
    left: '-3px',
  },
  resizerActive: {
    backgroundColor: tokens.colorBrandBackground,
  },

  // Panel header
  panelHeader: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  panelTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Panel content
  panelContent: {
    flex: 1,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    overflow: 'auto',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...customScrollbarStyles,
  },

  // Tab list container with overflow handling
  tabListContainer: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalXS,
    flexShrink: 0,
    minWidth: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  // Tab list
  tabList: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  // Overflow menu button
  overflowButton: {
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalXS,
  },

  // Editor content (scrollable)
  editorContent: {
    flex: 1,
    minHeight: 0, // Critical: allows flex children to shrink below content size
    overflow: 'auto',
    padding: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    ...customScrollbarStyles,
  },

  /**
   * Read-only mode for the form area.
   *
   * Strategy:
   *   - The Settings tab passes `readOnly={isReadOnly}` to SettingsPanel
   *     so Fluent's own native disabled state renders cleanly for
   *     Switch / SpinButton / Field (no grey-blob bleed).
   *   - The Parameters tab relies on store-level guards in
   *     nodeEditorStore (every mutating action early-returns when
   *     isReadOnly is true) plus TokenizedInput's own store gate.
   *   - We add a global `opacity` fade for visual feedback and block
   *     pointer events on text-like form controls so users don't waste
   *     clicks. We deliberately exclude Switch/Checkbox/Radio inputs
   *     here because Fluent's Switch renders a hidden
   *     `<input type="checkbox">` that would pick up the grey if we
   *     used a broad `& input` selector.
   */
  editorContentReadOnly: {
    opacity: 0.9,
    // Disable interaction on text-like editable controls without
    // recoloring them (Fluent already styles disabled states via the
    // readOnly prop / store guards).
    '& input[type="text"], & input[type="number"], & input[type="email"], & input[type="search"], & input[type="url"], & input[type="tel"], & input[type="password"], & input:not([type]), & textarea, & [contenteditable="true"], & [role="combobox"], & [role="listbox"], & [role="spinbutton"], & [role="slider"]':
      {
        pointerEvents: 'none',
        cursor: 'not-allowed',
      },
  },

  // Data tree
  dataTree: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalS,
    overflow: 'auto',
    flex: 1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },

  // Status section
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalM,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  statusLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  statusValue: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
  },
  statusSuccess: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusRunning: {
    color: tokens.colorPaletteBlueForeground2,
  },

  // Run history
  runHistory: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  runHistoryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
  },
  runHistoryNumber: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    width: '32px',
  },
  runHistoryStatus: {
    fontSize: tokens.fontSizeBase200,
  },
  runHistoryDuration: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginLeft: 'auto',
  },



  // Panel actions
  panelActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },

  // Empty state
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
  },

  // Settings tab styles
  settingsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  settingsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  settingsGroupTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXS,
  },
  settingsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  settingsLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  settingsDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  // About tab styles
  aboutSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  aboutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  aboutIcon: {
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: tokens.fontSizeHero700,
  },
  aboutIconDefault: {
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground2,
  },
  aboutInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
  },
  aboutName: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  aboutType: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  aboutDetailRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
  },
  aboutDetailLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minWidth: '100px',
  },
  aboutDetailValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  aboutDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
  },
  // Debug mode indicator
  debugModeHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorPalettePurpleBackground2,
  },
  debugModeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorPalettePurpleForeground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
  },

  panelContentCentered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Compact (narrow window) layout — 3 outer tabs replacing the 3 side-by-side panels
  compactLayout: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  compactTabBar: {
    flexShrink: 0,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    paddingLeft: tokens.spacingHorizontalM,
  },
  compactTabContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  compactPanelContent: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    ...customScrollbarStyles,
  },

  // Hidden strip used to measure actual tab widths so the overflow calculation
  // matches the real rendered size of each label.
  tabMeasurement: {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    height: 0,
    overflow: 'hidden',
  },
});

// Approximate width per tab (including padding)
const OVERFLOW_BUTTON_WIDTH = 40;

/**
 * ExpandedView - The fullscreen three-panel layout for node editing
 */
// Panel size constraints
const COMPACT_BREAKPOINT = 900; // Switch to tabbed layout when narrower than this
const LEFT_PANEL_MIN = 200;
const LEFT_PANEL_DEFAULT = 320;
const LEFT_PANEL_MAX = 600;
const RIGHT_PANEL_MIN = 200;
const RIGHT_PANEL_DEFAULT = 360;
const RIGHT_PANEL_MAX = 600;

export const ExpandedView = () => {
  const styles = useStyles();
  const initialTab = useNodeEditorStore((state) => state.initialTab);
  const clearInitialTab = useNodeEditorStore((state) => state.clearInitialTab);
  const inputPanelValueSource = useNodeEditorStore((state) => state.inputPanelValueSource);
  const isReadOnly = useNodeEditorStore((state) => state.isReadOnly);
  const setInputPanelValueSource = useNodeEditorStore(
    (state) => state.setInputPanelValueSource,
  );
  const [activeTab, setActiveTab] = useState<EditorTab>(initialTab ?? 'parameters');

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(LEFT_PANEL_DEFAULT);
  const [rightPanelWidth, setRightPanelWidth] = useState(RIGHT_PANEL_DEFAULT);
  const [resizingPanel, setResizingPanel] = useState<'left' | 'right' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Compact layout state
  const [containerWidth, setContainerWidth] = useState(0);
  const [outerTab, setOuterTab] = useState<'input' | 'configure' | 'output'>('configure');

  // Handle resize start
  const handleResizeStart = useCallback((panel: 'left' | 'right', e: React.MouseEvent) => {
    e.preventDefault();
    setResizingPanel(panel);
    startXRef.current = e.clientX;
    startWidthRef.current = panel === 'left' ? leftPanelWidth : rightPanelWidth;
  }, [leftPanelWidth, rightPanelWidth]);

  // Handle resize move and end
  useEffect(() => {
    if (!resizingPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;

      if (resizingPanel === 'left') {
        // Left panel: dragging right increases width
        const newWidth = Math.min(LEFT_PANEL_MAX, Math.max(LEFT_PANEL_MIN, startWidthRef.current + delta));
        setLeftPanelWidth(newWidth);
      } else {
        // Right panel: dragging left increases width
        const newWidth = Math.min(RIGHT_PANEL_MAX, Math.max(RIGHT_PANEL_MIN, startWidthRef.current - delta));
        setRightPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setResizingPanel(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingPanel]);

  // Clear the initial tab after using it (so subsequent opens don't retain it)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
      clearInitialTab();
    }
  }, [initialTab, clearInitialTab]);

  // Track container width to decide between 3-panel and compact tabbed layout
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isCompact = containerWidth > 0 && containerWidth < COMPACT_BREAKPOINT;

  // Shared node-editor derivations (variant flags, available tabs, settings
  // bundle, live preview). The tab bodies consume the same context so the
  // legacy view and the new drawer render identical content.
  const ctx = useNodeEditorContext();
  const { node, nodeType, availableTabs, showConnectionTab } = ctx;

  // Connection-first default landing (mirrors the drawer's connection-first
  // behavior). On the *first* mount for this editor open, if the caller didn't
  // request a specific tab AND this node requires a connection AND no
  // connection is set yet, land on Connection. This runs once per open so
  // subsequent connection-state changes don't yank the user off whatever tab
  // they navigated to.
  const didSeedDefaultTabRef = useRef(false);
  useLayoutEffect(() => {
    if (didSeedDefaultTabRef.current) return;
    didSeedDefaultTabRef.current = true;
    if (initialTab) return;
    if (ctx.showConnectionTab && !ctx.connectionName) {
      setActiveTab('connection');
    }
    // Runs once per mount. `ctx` values captured on first paint are correct
    // for the "opening this node" moment we're seeding for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get execution mode state for debug mode
  const isExecutionMode = useEditorRunStore((state) => state.isExecutionMode);

  // Dynamic-content token sources + run-data previews for this node, shared
  // with the compact ParameterList and the field-fork popover via
  // `useNodeTokenSources` so the inline `${…}` autocomplete, picker rail, and
  // popover stay in lockstep. The hook reads the draft agent parameter schema
  // itself so live Tool-tab edits flow into tokens without a panel reopen.
  const {
    tokenSources,
    combinedOutputs: combinedPreviewOutputs,
    hasRunData: hasPreviewData,
    isLoadingRunData: isLoadingPreviewData,
    tokensLoading,
    upstreamLoading,
  } = useNodeTokenSources(node);

  // Flatten once per panel; every TokenizedInput consumes these directly.
  const dynamicContentSuggestions = useMemo(
    () => tokenSourcesToSuggestions(tokenSources),
    [tokenSources],
  );

  // Open token picker targeting a new field with this token pre-selected.
  // For now, copy to clipboard for easy paste (silent on failure).
  const handleTokenClick = useCallback((token: Token) => {
    if (token.value) {
      navigator.clipboard.writeText(token.value).catch(() => {
        // Clipboard API may be unavailable (insecure context, missing perms).
      });
    }
  }, []);

  // Function-template insert from the rail's Expressions tab. Lands the
  // template in the parameter that most recently held focus; falls back to
  // clipboard when no field is focused (e.g. rail opened by keyboard before
  // any field was active).
  const handleExpressionInsert = useCallback((template: string) => {
    if (!template) return;
    const lastFocused =
      useNodeEditorStore.getState().lastFocusedParameterField;
    if (lastFocused) {
      useNodeEditorStore.getState().insertExpressionToParameter(lastFocused, template);
      return;
    }
    console.debug(
      '[ExpandedView] No focused parameter field for expression insert — falling back to clipboard',
    );
    navigator.clipboard.writeText(template).catch(() => {
      // Clipboard API may be unavailable (insecure context, missing perms).
      // Silent failure matches handleTokenClick — the function picker has
      // its own visible Insert button, so users can retry from there.
    });
  }, []);

  useEffect(() => {
    if (availableTabs.length === 0) {
      return;
    }

    const hasActiveTab = availableTabs.some(tab => tab.id === activeTab);
    if (!hasActiveTab && availableTabs[0]) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab, setActiveTab]);

  // Tab overflow: measures actual label widths via a hidden strip rendered
  // inside renderTabStrip(). The same strip is mounted in either the
  // compact or three-panel layout (never both), so the hook's ref
  // callbacks rebind cleanly on layout switches.
  const {
    setContainerRef: setTabContainerRef,
    setMeasurementRef: setTabMeasurementRef,
    visibleTabs,
    overflowTabs,
  } = useTabOverflow(availableTabs, activeTab, OVERFLOW_BUTTON_WIDTH);
  const hasOverflow = overflowTabs.length > 0;

  // Early return after all hooks
  if (!node) return null;

  // Editor tab strip with overflow handling. The hidden measurement strip
  // renders every available tab at natural width so useTabOverflow can
  // decide which fit. Only one instance of this is mounted at a time
  // (compact vs three-panel layout).
  const renderTabStrip = () => (
    <div className={styles.tabListContainer} ref={setTabContainerRef}>
      <TabList
        className={styles.tabList}
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as EditorTab)}
      >
        {visibleTabs.map(tab => (
          <Tab key={tab.id} value={tab.id}>{tab.label}</Tab>
        ))}
      </TabList>
      {hasOverflow && (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content="More tabs" relationship="label">
              <Button
                className={styles.overflowButton}
                appearance="subtle"
                icon={<MoreHorizontalRegular />}
                size="small"
                aria-label="More tabs"
              />
            </Tooltip>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {overflowTabs.map(tab => (
                <MenuItem key={tab.id} onClick={() => setActiveTab(tab.id)}>
                  {tab.label}
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
      <div className={styles.tabMeasurement} ref={setTabMeasurementRef} aria-hidden="true">
        <TabList selectedValue={activeTab}>
          {availableTabs.map(tab => (
            <Tab key={`measure-${tab.id}`} value={`measure-${tab.id}`} tabIndex={-1}>{tab.label}</Tab>
          ))}
        </TabList>
      </div>
    </div>
  );

  return (
    <DynamicContentTokensContext.Provider value={dynamicContentSuggestions}>
    <div className={styles.container} ref={containerRef}>
      {isCompact ? (
        /* Compact mode: 3 outer tabs replacing the 3 side-by-side panels */
        <div className={styles.compactLayout}>
          <div className={styles.compactTabBar}>
            <TabList
              selectedValue={outerTab}
              onTabSelect={(_, data) => setOuterTab(data.value as typeof outerTab)}
            >
              <Tab value="input">Input</Tab>
              <Tab value="configure">Configure</Tab>
              <Tab value="output">Output</Tab>
            </TabList>
          </div>
          <div className={styles.compactTabContent}>
            {/* Input tab */}
            {outerTab === 'input' && (
              tokensLoading || upstreamLoading ? (
                <div className={mergeClasses(styles.compactPanelContent, styles.panelContentCentered)}>
                  <Spinner size="small" label="Loading outputs..." />
                </div>
              ) : (
                <WorkflowInputPanel
                  sources={tokenSources}
                  onTokenClick={handleTokenClick}
                  showSearch={true}
                  currentNodeId={node?.id}
                  actionOutputs={combinedPreviewOutputs}
                  hasRunData={hasPreviewData}
                  isLoadingRunData={isLoadingPreviewData}
                  valueSource={inputPanelValueSource}
                  onValueSourceChange={setInputPanelValueSource}
                  onExpressionInsert={handleExpressionInsert}
                />
              )
            )}
            {/* Editor tab */}
            {outerTab === 'configure' && (
              <div className={mergeClasses(styles.centerPanel, styles.centerPanelShrinkable)}>
                {renderTabStrip()}
                <div className={mergeClasses(styles.editorContent, isReadOnly && styles.editorContentReadOnly)}>
                  {activeTab === 'parameters' && showConnectionTab && (
                    <ConnectionErrorBanner
                      nodeId={node.id}
                      {...(nodeType !== undefined ? { operationType: nodeType } : {})}
                      onNavigateToConnection={() => setActiveTab('connection')}
                    />
                  )}
                  <NodeEditorTabBody
                    tab={activeTab}
                    ctx={ctx}
                    onNavigateToHarness={() => setActiveTab('agent-harness')}
                  />
                </div>
              </div>
            )}
            {/* Output tab */}
            {outerTab === 'output' && (
              isExecutionMode ? (
                <>
                  <div className={styles.debugModeHeader}>
                    <div className={styles.debugModeLabel}>
                      <BugRegular />
                      <span>Run Data</span>
                    </div>
                    <Badge appearance="filled" color="informative" size="small">
                      Debug Mode
                    </Badge>
                  </div>
                  <RunDataPanel
                    nodeId={node.id}
                    nodeName={node.name}
                  />
                </>
              ) : (
                <div className={styles.compactPanelContent}>
                  <div className={styles.emptyState}>
                    <Text>No output data</Text>
                    <Text size={200}>Run the workflow to see action output</Text>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      ) : (
        <div className={styles.mainContent}>
          {/* Left Panel - Input (data from previous nodes) */}
          <div className={styles.leftPanel} style={{ width: leftPanelWidth }}>
          <div className={styles.panelHeader}>
            <Text className={styles.panelTitle}>Input</Text>
          </div>
          {tokensLoading || upstreamLoading ? (
            <div className={mergeClasses(styles.panelContent, styles.panelContentCentered)}>
              <Spinner size="small" label="Loading outputs..." />
            </div>
          ) : (
            <WorkflowInputPanel
              sources={tokenSources}
              onTokenClick={handleTokenClick}
              showSearch={true}
              currentNodeId={node?.id}
              actionOutputs={combinedPreviewOutputs}
              hasRunData={hasPreviewData}
              isLoadingRunData={isLoadingPreviewData}
              valueSource={inputPanelValueSource}
              onValueSourceChange={setInputPanelValueSource}
              onExpressionInsert={handleExpressionInsert}
            />
          )}
          {/* Resizer handle */}
          <div
            className={mergeClasses(styles.resizer, styles.resizerLeft, resizingPanel === 'left' && styles.resizerActive)}
            onMouseDown={(e) => handleResizeStart('left', e)}
            role="separator"
            aria-label="Resize input panel"
            aria-orientation="vertical"
          />
        </div>

        {/* Center Panel - Editor */}
        <div className={styles.centerPanel}>
          {renderTabStrip()}

          <div className={mergeClasses(styles.editorContent, isReadOnly && styles.editorContentReadOnly)}>
            {/* Connection error banner - only on Parameters tab for operations that need connections */}
            {activeTab === 'parameters' && showConnectionTab && (
              <ConnectionErrorBanner
                nodeId={node.id}
                {...(nodeType !== undefined ? { operationType: nodeType } : {})}
                onNavigateToConnection={() => setActiveTab('connection')}
              />
            )}
            <NodeEditorTabBody
              tab={activeTab}
              ctx={ctx}
              onNavigateToHarness={() => setActiveTab('agent-harness')}
            />
          </div>
        </div>

        {/* Right Panel - Output / Debug Data */}
        <div className={styles.rightPanel} style={{ width: rightPanelWidth }}>
          {/* Resizer handle */}
          <div
            className={mergeClasses(styles.resizer, styles.resizerRight, resizingPanel === 'right' && styles.resizerActive)}
            onMouseDown={(e) => handleResizeStart('right', e)}
            role="separator"
            aria-label="Resize output panel"
            aria-orientation="vertical"
          />
          {isExecutionMode ? (
            // Debug mode: show RunDataPanel with expression evaluations
            <>
              <div className={styles.debugModeHeader}>
                <div className={styles.debugModeLabel}>
                  <BugRegular />
                  <span>Run Data</span>
                </div>
                <Badge appearance="filled" color="informative" size="small">
                  Debug Mode
                </Badge>
              </div>
              <RunDataPanel
                nodeId={node.id}
                nodeName={node.name}
              />
            </>
          ) : (
            // Normal mode: show output panel (populated during workflow runs)
            <>
              <div className={styles.panelHeader}>
                <Text className={styles.panelTitle}>Output</Text>
              </div>
              <div className={styles.panelContent}>
                <div className={styles.emptyState}>
                  <Text>No output data</Text>
                  <Text size={200}>Run the workflow to see action output</Text>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      )}
    </div>
    </DynamicContentTokensContext.Provider>
  );
};

ExpandedView.displayName = 'ExpandedView';
